import React from 'react';
import { Mail, CheckCircle, ArrowRight, AlertTriangle, Zap } from 'lucide-react';
import ModernDocHeader from '../../components/documentation/ModernDocHeader';

const DripCampaignsDocs: React.FC = () => {
  return (
    <div>
      <ModernDocHeader
        title="Drip Campaigns"
        description="Automate email sequences to nurture leads and move them through your sales funnel with personalized, timed messages that convert."
        icon={<Mail className="w-6 h-6" />}
        badge="Automation"
        badgeColor="orange"
      />

      <div className="p-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 max-w-4xl">
          <div className="prose prose-blue dark:prose-invert max-w-none">

      {/* What You'll Learn */}
      <div className="not-prose bg-green-50 dark:bg-blue-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6">
        <div className="flex items-start space-x-3">
          <Zap className="w-5 h-5 text-brandColor dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              What You'll Learn
            </h3>
            <ul className="space-y-1 text-sm text-green-800 dark:text-green-300">
              <li>✓ Understanding drip campaigns and email automation</li>
              <li>✓ Creating effective email sequences</li>
              <li>✓ Setting up triggers and enrollment conditions</li>
              <li>✓ Personalizing emails with merge tags</li>
              <li>✓ Tracking campaign performance and engagement</li>
              <li>✓ Optimizing campaigns for better conversion</li>
            </ul>
          </div>
        </div>
      </div>

      <h2>What is a Drip Campaign?</h2>
      <p>
        A drip campaign is a series of automated emails sent to leads on a predetermined schedule.
        Instead of manually sending individual emails, you create a sequence once and the system
        sends emails automatically based on triggers you define.
      </p>

      <h3>Why Use Drip Campaigns?</h3>
      <ul>
        <li><strong>Save Time</strong> - Automate repetitive follow-up emails</li>
        <li><strong>Stay Consistent</strong> - Never forget to follow up with a lead</li>
        <li><strong>Nurture Leads</strong> - Guide prospects through the sales journey</li>
        <li><strong>Increase Engagement</strong> - Keep your brand top-of-mind</li>
        <li><strong>Improve Conversion</strong> - Timed messages at optimal moments</li>
        <li><strong>Scale Outreach</strong> - Handle hundreds of leads without more staff</li>
      </ul>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-6">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>💡 Did You Know?</strong>
        </p>
        <p className="text-sm text-gray-700 dark:text-gray-300">
          Companies that use drip campaigns see up to 80% increase in open rates and 3x higher
          click-through rates compared to standalone broadcast emails.
        </p>
      </div>

      <h2>Common Drip Campaign Types</h2>

      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
        <div className="p-4 border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-blue-900/20 rounded-lg">
          <h4 className="font-semibold text-green-900 dark:text-green-200 mb-2">Welcome Series</h4>
          <p className="text-sm text-brandColor dark:text-green-300 mb-3">
            Greet new leads and introduce your business
          </p>
          <ul className="text-xs text-brandColor dark:text-green-400 space-y-1">
            <li>• Day 1: Welcome & thank you</li>
            <li>• Day 3: Company overview</li>
            <li>• Day 7: Product/service highlights</li>
            <li>• Day 14: Success stories</li>
          </ul>
        </div>

        <div className="p-4 border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 rounded-lg">
          <h4 className="font-semibold text-green-900 dark:text-green-200 mb-2">Lead Nurturing</h4>
          <p className="text-sm text-green-700 dark:text-green-300 mb-3">
            Educate and build trust over time
          </p>
          <ul className="text-xs text-green-600 dark:text-green-400 space-y-1">
            <li>• Week 1: Educational content</li>
            <li>• Week 2: Case studies</li>
            <li>• Week 3: Product demo offer</li>
            <li>• Week 4: Limited-time offer</li>
          </ul>
        </div>

        <div className="p-4 border-2 border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
          <h4 className="font-semibold text-purple-900 dark:text-purple-200 mb-2">Re-engagement</h4>
          <p className="text-sm text-purple-700 dark:text-purple-300 mb-3">
            Win back inactive or cold leads
          </p>
          <ul className="text-xs text-purple-600 dark:text-purple-400 space-y-1">
            <li>• Email 1: "We miss you"</li>
            <li>• Email 2: Special offer</li>
            <li>• Email 3: What's new</li>
            <li>• Email 4: Last chance</li>
          </ul>
        </div>

        <div className="p-4 border-2 border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
          <h4 className="font-semibold text-orange-900 dark:text-orange-200 mb-2">Post-Sale</h4>
          <p className="text-sm text-orange-700 dark:text-orange-300 mb-3">
            Onboard new customers and encourage referrals
          </p>
          <ul className="text-xs text-orange-600 dark:text-orange-400 space-y-1">
            <li>• Day 1: Thank you & next steps</li>
            <li>• Day 7: Tips & best practices</li>
            <li>• Day 30: Satisfaction survey</li>
            <li>• Day 60: Referral request</li>
          </ul>
        </div>
      </div>

      <h2>Creating Your First Drip Campaign</h2>

      <h3>Step 1: Plan Your Campaign</h3>
      <p>Before building in the system, outline your campaign strategy:</p>
      <ol>
        <li><strong>Define Goal:</strong> What do you want to achieve? (Demo booking, trial signup, purchase)</li>
        <li><strong>Identify Audience:</strong> Who receives this campaign? (New leads, cold leads, post-demo)</li>
        <li><strong>Map Journey:</strong> What steps lead to your goal?</li>
        <li><strong>Craft Messages:</strong> What value does each email provide?</li>
        <li><strong>Set Timing:</strong> How long between emails? (1 day, 3 days, 1 week)</li>
      </ol>

      <h3>Step 2: Create Campaign in CRM</h3>
      <ol>
        <li>Navigate to <code>Settings → Drip Campaigns</code></li>
        <li>Click "Create New Campaign"</li>
        <li>Enter campaign name (e.g., "New Lead Welcome Series")</li>
        <li>Add description for internal reference</li>
        <li>Set campaign status to "Draft" initially</li>
        <li>Click "Create"</li>
      </ol>

      <h3>Step 3: Configure Enrollment Triggers</h3>
      <p>Define what causes a lead to enter this campaign:</p>

      <div className="not-prose space-y-3 my-6">
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Manual Enrollment</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Admin or sales rep manually adds specific leads to the campaign
          </p>
        </div>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Form Submission</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Automatically enroll when lead fills website form, downloads resource, or signs up
          </p>
        </div>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Lead Source</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            All leads from specific source (Facebook, Website, Phone) automatically enrolled
          </p>
        </div>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Pipeline Stage Change</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            When lead moves to specific stage (e.g., enters "Qualified" → start nurture campaign)
          </p>
        </div>

        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Time-Based</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Leads with no activity for X days automatically enter re-engagement campaign
          </p>
        </div>
      </div>

      <h3>Step 4: Build Email Sequence</h3>
      <p>Add emails to your campaign:</p>

      <ol>
        <li>Click "Add Email" button</li>
        <li>Set delay/timing:
          <ul>
            <li><strong>Immediate</strong> - Sent right when enrolled</li>
            <li><strong>Wait X Days</strong> - Sent after specified days from previous email</li>
            <li><strong>Wait X Hours</strong> - Sent after specified hours</li>
            <li><strong>Wait Until Specific Day</strong> - E.g., next Monday at 9 AM</li>
          </ul>
        </li>
        <li>Write email subject line (keep under 50 characters for mobile)</li>
        <li>Compose email body using editor</li>
        <li>Add personalization tags (more on this below)</li>
        <li>Include clear call-to-action (CTA)</li>
        <li>Click "Save Email"</li>
        <li>Repeat for each email in sequence</li>
      </ol>

      <div className="not-prose bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 my-6">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm text-yellow-800 dark:text-yellow-300 mb-2">
              <strong>⚠️ Best Practice:</strong>
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              Limit sequences to 5-7 emails. Too many emails = higher unsubscribe rates.
              If lead doesn't engage after 7 emails, they're likely not interested.
            </p>
          </div>
        </div>
      </div>

      <h3>Step 5: Personalize with Merge Tags</h3>
      <p>Make emails feel personal by inserting lead data:</p>

      <div className="not-prose">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden my-6">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Merge Tag</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Replaced With</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Example</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-3"><code className="text-xs">{"{{name}}"}</code></td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Lead's name</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">Hi Rajesh,</td>
              </tr>
              <tr>
                <td className="px-4 py-3"><code className="text-xs">{"{{email}}"}</code></td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Lead's email</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">rajesh@example.com</td>
              </tr>
              <tr>
                <td className="px-4 py-3"><code className="text-xs">{"{{phone}}"}</code></td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Lead's phone</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">+91 98765 43210</td>
              </tr>
              <tr>
                <td className="px-4 py-3"><code className="text-xs">{"{{company}}"}</code></td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Company name</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">Tech Solutions Ltd</td>
              </tr>
              <tr>
                <td className="px-4 py-3"><code className="text-xs">{"{{source}}"}</code></td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Lead source</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">Facebook</td>
              </tr>
              <tr>
                <td className="px-4 py-3"><code className="text-xs">{"{{assigned_to}}"}</code></td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">Assigned rep name</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">Priya Sharma</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <p><strong>Example personalized email:</strong></p>
      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-4">
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
          <strong>Subject:</strong> Hi {"{{name}}"}, here's what we discussed
        </p>
        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-2 font-mono">
          <p>Hi {"{{name}}"},</p>
          <p>
            Thanks for your interest in our services! I noticed you came through {"{{source}}"}.
          </p>
          <p>
            I'm {"{{assigned_to}}"}, and I'll be your main point of contact. Feel free to reach
            me directly at this email or call me at +91-XXX-XXX-XXXX.
          </p>
          <p>Looking forward to helping {"{{company}}"} succeed!</p>
        </div>
      </div>

      <h3>Step 6: Test Campaign</h3>
      <ol>
        <li>Click "Send Test Email" button</li>
        <li>Enter your email address</li>
        <li>Review how emails look with real data</li>
        <li>Check merge tags are working correctly</li>
        <li>Verify links and CTAs work</li>
        <li>Test on mobile and desktop</li>
      </ol>

      <h3>Step 7: Activate Campaign</h3>
      <ol>
        <li>Review all emails in sequence</li>
        <li>Confirm triggers are configured correctly</li>
        <li>Set campaign status to "Active"</li>
        <li>New leads matching criteria will auto-enroll</li>
      </ol>

      <h2>Managing Active Campaigns</h2>

      <h3>Unenrollment Rules</h3>
      <p>Leads automatically stop receiving emails when:</p>
      <ul>
        <li><strong>Campaign Completed</strong> - Finished all emails in sequence</li>
        <li><strong>Unsubscribed</strong> - Clicked unsubscribe link</li>
        <li><strong>Marked as Lost</strong> - Lead status changed to Lost</li>
        <li><strong>Marked as Won</strong> - Lead converted (optional setting)</li>
        <li><strong>Manual Unenrollment</strong> - Admin removes from campaign</li>
        <li><strong>Bounced Email</strong> - Email address invalid</li>
      </ul>

      <h3>Pausing vs Stopping</h3>
      <div className="not-prose grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Pause Campaign</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Temporarily stop sending emails
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <li>• Enrolled leads remain in campaign</li>
            <li>• No new enrollments</li>
            <li>• Resume anytime</li>
            <li>• Use for: Holidays, maintenance</li>
          </ul>
        </div>

        <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Stop Campaign</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            Permanently end campaign
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
            <li>• All leads unenrolled</li>
            <li>• Cannot be restarted</li>
            <li>• Data archived</li>
            <li>• Use for: Obsolete campaigns</li>
          </ul>
        </div>
      </div>

      <h2>Tracking Campaign Performance</h2>

      <h3>Key Metrics to Monitor</h3>
      <div className="not-prose">
        <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden my-6">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Metric</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">What It Means</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-white">Good Benchmark</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Open Rate</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">% of emails opened</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">20-30%</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Click Rate</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">% who clicked links</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">2-5%</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Reply Rate</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">% who replied to email</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">5-10%</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Unsubscribe Rate</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">% who opted out</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{"<"}0.5%</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Conversion Rate</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">% who completed goal</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">1-3%</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-medium">Bounce Rate</td>
                <td className="px-4 py-3 text-gray-700 dark:text-gray-300">% of failed deliveries</td>
                <td className="px-4 py-3 text-gray-600 dark:text-gray-400">{"<"}2%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <h3>Analyzing Results</h3>
      <p>Access campaign analytics from <code>Reports → Drip Campaigns</code>:</p>
      <ul>
        <li>Overall campaign performance summary</li>
        <li>Email-by-email breakdown</li>
        <li>Engagement trends over time</li>
        <li>Best performing subject lines</li>
        <li>Optimal send times</li>
        <li>Conversion funnel visualization</li>
      </ul>

      <h2>Optimization Tips</h2>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 my-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-3">
              Email Best Practices
            </h3>
            <ul className="space-y-2 text-sm text-green-800 dark:text-green-300">
              <li>✓ <strong>Write compelling subject lines</strong> - Create curiosity without clickbait</li>
              <li>✓ <strong>Keep emails concise</strong> - Aim for 150-200 words max</li>
              <li>✓ <strong>One clear CTA</strong> - Don't overwhelm with multiple asks</li>
              <li>✓ <strong>Provide value first</strong> - Don't just pitch, educate and help</li>
              <li>✓ <strong>Mobile-friendly format</strong> - 60%+ emails opened on mobile</li>
              <li>✓ <strong>Test send times</strong> - Tuesday-Thursday 10 AM often performs best</li>
              <li>✓ <strong>A/B test subject lines</strong> - Small tweaks = big impact</li>
              <li>✓ <strong>Include unsubscribe link</strong> - Required by law, builds trust</li>
            </ul>
          </div>
        </div>
      </div>

      <h3>Improving Open Rates</h3>
      <ul>
        <li>Personalize subject lines with name or company</li>
        <li>Keep subject lines under 50 characters</li>
        <li>Use numbers and questions ("3 Ways to..." or "Ready to...")</li>
        <li>Avoid spam trigger words (free, guarantee, limited time)</li>
        <li>Test different send times for your audience</li>
        <li>Clean email list regularly (remove bounces)</li>
      </ul>

      <h3>Boosting Click Rates</h3>
      <ul>
        <li>Use button CTAs instead of text links</li>
        <li>Place CTA above the fold</li>
        <li>Make CTA action-oriented ("Get Your Free Demo" not "Click Here")</li>
        <li>Use contrasting colors for buttons</li>
        <li>Include only one primary CTA per email</li>
        <li>Add urgency when appropriate ("Limited spots")</li>
      </ul>

      <h2>Advanced Campaign Features</h2>

      <h3>A/B Testing</h3>
      <p>Test different variations to find what works best:</p>
      <ol>
        <li>Create campaign as normal</li>
        <li>Enable "A/B Test" toggle</li>
        <li>Choose what to test:
          <ul>
            <li>Subject line</li>
            <li>Sender name</li>
            <li>Email content</li>
            <li>Send time</li>
          </ul>
        </li>
        <li>Set split % (e.g., 50/50 or 80/20)</li>
        <li>Define success metric (opens, clicks, replies)</li>
        <li>System sends both versions and tracks winner</li>
      </ol>

      <h3>Conditional Logic / Branching</h3>
      <p>Send different emails based on lead behavior:</p>
      <ul>
        <li><strong>If opened:</strong> Send follow-up content</li>
        <li><strong>If not opened:</strong> Send reminder with different subject</li>
        <li><strong>If clicked link:</strong> Send detailed product info</li>
        <li><strong>If not clicked:</strong> Send benefit-focused email</li>
      </ul>

      <div className="not-prose bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 my-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          <strong>Example Branching Flow:</strong>
        </p>
        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1 font-mono">
          <p>Day 1: Welcome email (sent to all)</p>
          <p className="pl-4">↓ Opened? →</p>
          <p className="pl-8">Yes: Day 3 send "Product Features" email</p>
          <p className="pl-8">No: Day 2 send "Did you see this?" re-send</p>
        </div>
      </div>

      <h3>Campaign Stacking</h3>
      <p>Enroll leads in multiple campaigns simultaneously:</p>
      <ul>
        <li>Welcome series (brand introduction)</li>
        <li>+ Educational newsletter (weekly tips)</li>
        <li>+ Product-specific drip (if they showed interest)</li>
      </ul>
      <p>
        <strong>Caution:</strong> Too many emails = fatigue. Monitor overall email frequency per lead.
      </p>

      <h2>Troubleshooting</h2>

      <div className="not-prose bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700 my-4">
        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Emails going to spam folder?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-2">
            <p><strong>Common causes & fixes:</strong></p>
            <ul className="list-disc list-inside space-y-1">
              <li>Avoid spam trigger words (free, guarantee, act now, limited time)</li>
              <li>Don't use ALL CAPS in subject line</li>
              <li>Reduce exclamation marks!!!</li>
              <li>Include plain text version (not just HTML)</li>
              <li>Verify sender domain is authenticated (SPF/DKIM)</li>
              <li>Ask recipients to whitelist your email</li>
            </ul>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Low open rates - what's wrong?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Diagnostic checklist:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Subject line too long or boring?</li>
              <li>Sender name unrecognizable?</li>
              <li>Sending at wrong time (3 AM local time)?</li>
              <li>Email list outdated or purchased (never buy lists!)</li>
              <li>Too many emails sent recently (fatigue)</li>
              <li>Preview text not compelling?</li>
            </ul>
            <p className="mt-2"><strong>Fix:</strong> Test different subject lines and send times</p>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Merge tags showing {"{{name}}"} instead of actual name?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>This happens when:</strong></p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li>Lead doesn't have that field filled (name is empty)</li>
              <li>Typo in merge tag syntax</li>
              <li>Using wrong tag format (check documentation)</li>
            </ul>
            <p className="mt-2"><strong>Solution:</strong> Set fallback values like {"{{name || 'there'}}"} → "Hi there"</p>
          </div>
        </details>

        <details className="p-4">
          <summary className="font-medium text-gray-900 dark:text-white cursor-pointer">
            Lead not receiving emails even though enrolled?
          </summary>
          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            <p><strong>Check lead's campaign status page:</strong></p>
            <ol className="list-decimal list-inside space-y-1 mt-2">
              <li>Is campaign set to "Active"? (not Draft or Paused)</li>
              <li>Has lead unsubscribed from emails?</li>
              <li>Is email address valid? (check for typos)</li>
              <li>Was lead manually unenrolled?</li>
              <li>Did previous email bounce?</li>
              <li>Check email logs for delivery errors</li>
            </ol>
          </div>
        </details>
      </div>

      <h2>Next Steps</h2>

      <div className="not-prose space-y-3 mt-6">
        <a
          href="/docs/lead-management"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Lead Management</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Understand lead data to create better targeted campaigns
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/pipeline"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Pipeline Stages</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Trigger campaigns based on pipeline stage changes
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>

        <a
          href="/docs/round-robin"
          className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-blue-500 dark:hover:border-blue-500 transition-colors"
        >
          <div>
            <p className="font-medium text-gray-900 dark:text-white">Round Robin Assignment</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Combine auto-assignment with email nurturing
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400" />
        </a>
      </div>

      <div className="not-prose bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 mt-8">
        <div className="flex items-start space-x-3">
          <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-green-900 dark:text-green-200 mb-2">
              Email Automation Activated!
            </h3>
            <p className="text-green-800 dark:text-green-300 mb-3">
              You're now equipped to create effective drip campaigns that nurture leads on autopilot.
              Remember: provide value first, be consistent, and always test & optimize.
            </p>
            <a
              href="/settings/drip-campaigns"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
            >
              <Mail className="w-4 h-4 mr-2" />
              Create Your First Campaign
            </a>
          </div>
        </div>
      </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default DripCampaignsDocs;
